<script setup lang="ts">
import { computed } from 'vue'

import type { Recommendation } from '../models/orpheus.types'

import { MetricLabel, useMetricCatalog, useReportCellFormatter } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button } from '@/shared/ui'
import { RichTextViewer } from '@/shared/ui/editor'

const props = withDefaults(defineProps<{ recommendation: Recommendation; busy?: boolean }>(), {
  busy: false,
})
const emit = defineEmits<{ confirm: [accepted: boolean] }>()
const { t } = useI18n()
const format = useFormat()
const catalog = useMetricCatalog()
const { formatCell } = useReportCellFormatter()

const metrics = computed(() =>
  Object.entries(props.recommendation.currentMetrics).map(([field, value]) => ({
    field,
    value,
    column: catalog.getColumn(field),
  })),
)
const category = computed(() =>
  props.recommendation.category
    ? props.recommendation.category.replaceAll('_', ' ')
    : t('orpheus.recommendation.categoryFallback'),
)
const confidence = computed(() =>
  props.recommendation.confidence === null
    ? ''
    : format.formatPercent(props.recommendation.confidence, { scale: 'ratio' }),
)
</script>

<template>
  <article
    class="recommendation-card grid gap-md p-md border border-orpheus-muted rounded-lg bg-surface shadow-[var(--ui-shadow-sm)]"
  >
    <header class="recommendation-card__header flex items-start justify-between gap-sm">
      <div class="recommendation-card__entity grid gap-2xs">
        <span
          class="recommendation-card__eyebrow m-0 text-muted text-[0.6875rem] font-semibold tracking-[0.08em] uppercase"
          >{{ t('orpheus.recommendation.searchTerm') }}</span
        >
        <strong>{{ recommendation.entity || t('orpheus.recommendation.valueMissing') }}</strong>
      </div>
      <span
        class="recommendation-card__category py-2xs px-xs rounded-full bg-orpheus-soft text-orpheus text-[0.75rem] font-semibold"
        >{{ category }}</span
      >
    </header>

    <dl
      v-if="metrics.length"
      class="recommendation-card__metrics grid grid-cols-[repeat(auto-fit,_minmax(7rem,_1fr))] gap-xs m-0"
    >
      <div
        v-for="metric in metrics"
        :key="metric.field"
        class="recommendation-card__metric min-w-0 p-xs rounded-md bg-surface-sunken"
      >
        <dt>
          <MetricLabel :field="metric.field" :column="metric.column ?? undefined" />
        </dt>
        <dd>{{ formatCell(metric.value, metric.column) }}</dd>
      </div>
    </dl>

    <section v-if="recommendation.reasoning" class="recommendation-card__section grid gap-2xs">
      <h4>{{ t('orpheus.recommendation.reasoning') }}</h4>
      <RichTextViewer :content="recommendation.reasoning" />
    </section>

    <section
      v-if="recommendation.suggestedAction"
      class="recommendation-card__section grid gap-2xs"
    >
      <h4>
        {{
          t(
            recommendation.requiresConfirmation
              ? 'orpheus.recommendation.confirmationNeeded'
              : 'orpheus.recommendation.action',
          )
        }}
      </h4>
      <RichTextViewer :content="recommendation.suggestedAction" />
      <div
        v-if="recommendation.requiresConfirmation"
        class="recommendation-card__actions flex items-start justify-between gap-sm justify-start mt-xs"
      >
        <Button size="sm" :disabled="busy" @click="emit('confirm', true)">
          {{ t('orpheus.recommendation.confirm') }}
        </Button>
        <Button size="sm" variant="outline" :disabled="busy" @click="emit('confirm', false)">
          {{ t('orpheus.recommendation.reject') }}
        </Button>
      </div>
    </section>

    <p
      v-if="confidence"
      class="recommendation-card__confidence m-0 text-muted text-[0.75rem] text-end"
    >
      {{ t('orpheus.recommendation.confidence', { value: confidence }) }}
    </p>
  </article>
</template>

<style scoped>
.recommendation-card .recommendation-card__section h4 {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
}
.recommendation-card .recommendation-card__metric dt {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
}
.recommendation-card .recommendation-card__metric dd {
  margin-top: var(--ui-space-2xs);
  margin-inline: 0;
  margin-bottom: 0;
  font-weight: 600;
}
</style>
